import { configureStore, StoreEnhancer } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import 'assets/fonts/icomoon.css';
import mapValuesDeep from 'map-values-deep';
import 'pure-react-carousel/dist/react-carousel.es.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { CombinedState } from 'redux';
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { ConditionalExcept, ValueOf } from 'type-fest';
import { NODE_ENV } from '../environment';
import rootReducer from './root-reducer';

const sentryReduxEnhancer =
  Sentry.createReduxEnhancer() as StoreEnhancer;

const persistTransform = createTransform<
  ValueOf<RootState>,
  ValueOf<RootState>
>(
  // transform state on its way to being serialized and persisted.
  inboundState =>
    // set all loading keys to zero to avoid hanging loading spinners
    // set fetchifyLoaded to false to prevent from using before reloaded
    // set appOnline to true to avoid any hanging 'backOnline' state
    mapValuesDeep(inboundState, (value, key) => {
      if (key === 'loading') return 0;
      if (key === 'fetchifyLoaded') return false;
      if (key === 'appOnline') return true;

      return value;
    })
);

export const persistConfig = {
  key: 'root',
  storage,
  transforms: [persistTransform],
};

const persistedReducer = persistReducer(
  persistConfig,
  rootReducer
) as unknown as typeof rootReducer;

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // NOTE: this ignores all redux-persist actions,
        // which are non-serialisable
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
        ],
      },
    }),
  enhancers: [sentryReduxEnhancer],
  devTools: NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

//
// ------------------- Type Exports ---------------------

export type FullStoreType = typeof store;
export type RootState = DeepStrip<StripNullKeys<StoreWrapped>>;
export type AppDispatch = FullStoreType['dispatch'];

//
// ------------------ Type Functions --------------------

type StripNullKeys<T> = ConditionalExcept<T, undefined>;

type IsWrapped<T> = T extends CombinedState<unknown> ? true : false;

type DeepStrip<T> = {
  [K in keyof T]: IsWrapped<T[K]> extends true
    ? DeepStrip<StripNullKeys<T[K]>>
    : T[K];
};

type StoreWrapped = ReturnType<FullStoreType['getState']>;

// ------------------------------------------------------
